import cloneDeep from 'lodash/cloneDeep';
import type { Item, LoadedItem, SearchResultHit } from '@mimir/common/dto/searchResultHit';
import type { RelatedItem } from '@mimir/common/dto/relationModels';
import type { QuickPreviewTabName } from '@mimir/common/types/UIOnlySections';
import type { MetadataSnapshot } from '@mimir/common/dto/mdfModels';
import type { ItemMediaPlayerOps, PostLoadSeek } from './itemMediaPlayerOps';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { ThumbnailClick, ThumbnailStripClick } from '../pages/search/components/search-page-interfaces';
import { getGlobalStore } from '@mimir/web/store';
import Vue, { reactive, type Ref, ref } from 'vue';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import { ItemState } from '@mimir/common/dto/itemState';
import { isDeleting } from '@mimir/common/dto/itemModels';
import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import { logErrorAndShowToast } from '../util/uiError';
import { OperationApiContext } from '@mimir/common/util/operationApiContext';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { isNumber } from 'lodash';
import { toRelatedItem } from './toRelatedItem';
import { getMediaDuration } from '@mimir/common/util/item';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import { ApiActions } from '@mimir/client-common/util/apiActions';
import { storeDispatchLoadCurrentItemMetadata } from '../store/storeDispatch';
import { getUISettings } from '../util/localStorage';

export interface LoadItemInQuickPreviewPayload {
  item: Item | null,
  fromHistory?: true,
  autoPlayFrom?: number,
  timeToSeekInMs?: number,
}

export interface LoadRelatedItemInQuickPreviewPayload {
  event: ThumbnailStripClick | ThumbnailClick,
}

export interface SetCollapsedPayload {
  shouldBeCollapsed: boolean,
}

export interface SetIsItemMetadataLoadingPayload {
  id: string,
  loading: boolean
}

export interface SetRelatedItemhasBeenUploadedToInRelationsPayload {
  id: string,
  hasBeenUploadedTo: boolean,
}

export interface SetSearchItemPayload {
  item: Item | null,
}

export interface UpdateCurrentPreviewItemPayload {
  id: string;
  update: Partial<LoadedItem>;
  retrievedFields?: (keyof LoadedItem)[]; // fields that were fetched from the server
}


interface MutableQuickPreviewState {
  currentItem: Ref<Item | null>,
  selectedRelatedItem: Ref<RelatedItem | null>,
  selectedTab: Ref<QuickPreviewTabName>,
  previewCollapsed: Ref<boolean>,
  recentlyPreviewed: Ref<Item[]>,
  isItemMetadataLoadingMap: Ref<Record<string, boolean>>,
}

export interface QuickPreviewState {
  currentItem: Readonly<Ref<Item | null>>,
  selectedRelatedItem: Readonly<Ref<RelatedItem | null>>,
  selectedTab: Readonly<Ref<QuickPreviewTabName>>,
  previewCollapsed: Readonly<Ref<boolean>>,
  recentlyPreviewed: Readonly<Ref<Item[]>>,
  isItemMetadataLoadingMap: Readonly<Ref<Record<string, boolean>>>,
}

const ensureReadOnly = (quickPreview: QuickPreview) => {
  // @ts-expect-error: If this line does not fail then the QuickPreview state
  // is no longer immutable.
  quickPreview.state.currentItem.value = null;
}
void ensureReadOnly;

export interface QuickPreviewOps {
  // Mutations
  setIsItemMetadataLoading(payload: SetIsItemMetadataLoadingPayload): void;
  setSelectedQuickPreviewTab(payload: QuickPreviewTabName): void;
  setSearchItem(payload: SetSearchItemPayload): void;
  updateCurrentPreviewItem(payload: UpdateCurrentPreviewItemPayload): void;
  setSearchPlayerItemMetadataSnapshot(data: MetadataSnapshot | undefined): void;

  // Actions
  updateQuickPreviewReference(hits: SearchResultHit[]): void;
  emitPlayerError(ev: ErrorEvent): void;
  setCollapsed(payload: SetCollapsedPayload): void;
  loadItemInQuickPreview(payload: LoadItemInQuickPreviewPayload): void;
  loadRelatedItemInQuickPreview(payload: LoadRelatedItemInQuickPreviewPayload): void;
  unloadSearchPlayer(): void;
  updateCurrentPreviewItemMutation(payload: UpdateCurrentPreviewItemPayload): void;
  getPlayer(): ItemMediaPlayerOps;
}

export interface QuickPreview {
  readonly state: QuickPreviewState,
  ops: QuickPreviewOps,
}

export function createQuickPreview(
  services: {
    quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
  }
): QuickPreview {
  const uiSettings = getUISettings();
  const initialSelectedTab: QuickPreviewTabName = uiSettings.thirdColumn !== 'metadata' ? 'metadata' : 'logging';
  const { quickPreviewMediaPlayerOps } = services;
  const mutableState : MutableQuickPreviewState = {
    currentItem: ref<Item | null>(null),
    selectedRelatedItem: ref<RelatedItem | null>(null),
    selectedTab: ref<QuickPreviewTabName>(initialSelectedTab),
    previewCollapsed: ref<boolean>(false),
    recentlyPreviewed: ref<Item[]>([]),
    // Wrap this reactive object in a Ref for consistency sake. Not
    // technically necessary.
    isItemMetadataLoadingMap: ref<Record<string, boolean>>(reactive({})),
  };

  const quickPreview = {
    get state() { return mutableState; },
    ops: {
      setIsItemMetadataLoading(payload: SetIsItemMetadataLoadingPayload): void {
        Vue.set(mutableState.isItemMetadataLoadingMap.value, payload.id, payload.loading);
      },
      setSelectedQuickPreviewTab(payload: QuickPreviewTabName): void {
        mutableState.selectedTab.value = payload;
      },
      setSearchItem(payload: SetSearchItemPayload): void {
        const { item } = payload;
        if (item && item.id === mutableState.currentItem.value?.id) {
          /**
           * TODO: If we already have this item as the currentItem, we should
           * probably updating the existing item instead of overwriting it, since
           * we mutate the currentItem with client state from various places.
           */
          console.warn('setSearchItem: item is already the current item');
        }

        if (item === undefined) {
          throw new Error('Item is undefined (not null). This the calling code is somehow violating the type safety');
        }

        mutableState.currentItem.value = cloneDeep(item);
        if (mutableState.currentItem.value !== null) {
          addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, mutableState.currentItem.value);
        }
      },
      updateCurrentPreviewItem(payload: UpdateCurrentPreviewItemPayload): void {
        if (!mutableState.currentItem.value || payload.id !== mutableState.currentItem.value.id) return;
        const keysToIterateOver = Object.keys(payload.update) as (keyof LoadedItem)[];
        const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
        const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && payload.update.metadata !== undefined) {
            let metadataVersion = payload.update.metadataVersion;
            if (metadataVersion == undefined) {
              console.warn('updateCurrentPreviewItem: metadata update without metadataVersion');
              metadataVersion = 0;
            }
            handleMdfUpdatesToItem(mutableState.currentItem.value, payload.update.metadata, metadataVersion, vueSet, vueDeleter);
          } else if (payload.update[prop] === undefined) {
            Vue.delete(mutableState.currentItem.value, prop);
          } else {
            Vue.set(mutableState.currentItem.value, prop, payload.update[prop]);
          }
        }
      },
      setSearchPlayerItemMetadataSnapshot(data: MetadataSnapshot | undefined): void {
        if (mutableState.currentItem.value) {
          Vue.set(mutableState.currentItem.value, 'metadataSnapshot', data);
        }
      },

      // Actions
      updateQuickPreviewReference(hits: SearchResultHit[]): void {
        const store = getGlobalStore();

        const currentItem = mutableState.currentItem.value;
        if (currentItem === null) return;
        const match = hits.find(hit => hit.id === currentItem.id);
        if (match) {
          // We need to make sure the currentItem has updated metaData.
          if (!mutableState.isItemMetadataLoadingMap.value[currentItem.id]) {
            storeDispatchLoadCurrentItemMetadata({ quickPreview }).catch((err) => `Error fetching currentItem metadata ${err}`);
          }
          // If we found a match, we should also mark it as selected.
          store.commit('addToSelectedSearchItems', match);
          addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, currentItem);
        }
      },
      emitPlayerError(ev: ErrorEvent): void {
        // Suppress error for now if its a placeholder. This should be removed once we have a quick preview
        // component for place holders (likely a drag and drop component. Issue #426)
        const item = mutableState.currentItem.value;
        if (item !== null &&
            item.itemType === ItemType.VIDEO &&
            item.itemState !== ItemState.NEW &&
            item.itemState !== ItemState.HIGHRES_AVAILABLE &&
            !isDeleting(item) &&
            item.transcodingState &&
            item.transcodingState !== AsyncOperationState.PROGRESSING &&
            item.transcodingState !== AsyncOperationState.INITIATED) {
          logErrorAndShowToast('Failed to load video', ev);
        }
      },
      setCollapsed(payload: SetCollapsedPayload): void {
        const { shouldBeCollapsed } = payload;
        if (shouldBeCollapsed) {
          quickPreviewMediaPlayerOps.pause();
        }
        mutableState.previewCollapsed.value = shouldBeCollapsed;
      },
      loadItemInQuickPreview(payload: LoadItemInQuickPreviewPayload): void {
        const store = getGlobalStore();
        const { item, fromHistory, autoPlayFrom, timeToSeekInMs } = payload;
        if (item !== null && quickPreviewMediaPlayerOps.getVisibleObs().currentValue() && (item.itemType === ItemType.VIDEO || item.itemType === ItemType.AUDIO)) {
          const apiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
          const apiActions = new ApiActions(apiContext);
          const autoplay = isNumber(autoPlayFrom) ? true : !quickPreviewMediaPlayerOps.getPausedObs().currentValue();
          const useNewBufferingLogic = (store.getters.featureFlags as TenantConfigDto['featureFlags'])?.useNewBufferingLogic ?? false;
          const flags = {
            useNewBufferingLogic,
          };
          const seekPositionInMs = (isNumber(autoPlayFrom) ? autoPlayFrom : (timeToSeekInMs ?? 0));
          const postLoadOperation: PostLoadSeek = {
            type: 'seek',
            positionInMs: seekPositionInMs,
            play: autoplay,
          };
          quickPreviewMediaPlayerOps.setItemId(apiActions, item.id, item.itemType, flags, { postLoadOperation });
        }
        mutableState.selectedRelatedItem.value = null;
        if (fromHistory) {
          // When navigating via history, to reduce user confusion we clear selection in search, because there's no guarantee that the item
          // that will be previewed will be visible in the current search result.
          store.commit('clearSelectedSearchItems');
        }
        this.setSearchItem({ item });
        return;
      },
      loadRelatedItemInQuickPreview(payload: LoadRelatedItemInQuickPreviewPayload): void {
        const store = getGlobalStore();
        const { event } = payload;
        const wasPlaying = !quickPreviewMediaPlayerOps.getPausedObs().currentValue();
        if (event.item !== mutableState.selectedRelatedItem.value) {
          const relatedItem = toRelatedItem(event.item);
          if (relatedItem === null) {
            throw new Error('Requested item to load is not a related item');
          }
          mutableState.selectedRelatedItem.value = relatedItem;
          addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, mutableState.selectedRelatedItem.value);
          if (event.item.itemType === ItemType.VIDEO || event.item.itemType === ItemType.AUDIO) {
            const apiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
            const apiActions = new ApiActions(apiContext);
            const useNewBufferingLogic = (store.getters.featureFlags as TenantConfigDto['featureFlags'])?.useNewBufferingLogic ?? false;
            const flags = {
              useNewBufferingLogic,
            };

            quickPreviewMediaPlayerOps.setItemId(apiActions, event.item.id, event.item.itemType, flags);
            mutableState.previewCollapsed.value = false;
          } else {
            quickPreviewMediaPlayerOps.clear();
          }
        }

        const mediaDuration = getMediaDuration(event.item);
        if (mediaDuration)  {
          let time = 0;
          if (isThumbnailStripClick(event) && mediaDuration) {
            time = mediaDuration * event.percentageOfVideo;
          }
          setTimeout(() => {
            if (wasPlaying) {
              quickPreviewMediaPlayerOps.play(time);
            } else {
              quickPreviewMediaPlayerOps.seekToVirtualClipMediaPosition({ approximate: time / 1000.0 });
            }
          }, 100);
        }
      },
      unloadSearchPlayer(): void {
        quickPreviewMediaPlayerOps.clear();
      },
      updateCurrentPreviewItemMutation(payload: UpdateCurrentPreviewItemPayload) {
        const currentItem = mutableState.currentItem.value;
        if (!currentItem || payload.id !== currentItem.id) return;

        /*
          `payload.retrievedFields` contains a list of keys fetched from the server.
          Since `undefined` values are not transmitted over the API, we combine the
          fetched keys with those available in the `payload.update` object.

          This ensures that the client state is updated with the most recent data
          while correctly handling stale state updates.
        */
        const uniqueKeys = new Set([...Object.keys(payload.update), ...(payload.retrievedFields ?? [])]);
        const keysToIterateOver = Array.from(uniqueKeys) as (keyof LoadedItem)[];

        const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
        const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && payload.update.metadata !== undefined) {
            let metadataVersion = payload.update.metadataVersion;
            if (metadataVersion == undefined) {
              console.warn('updateCurrentPreviewItemMutation: metadata update without metadataVersion');
              metadataVersion = 0;
            }
            handleMdfUpdatesToItem(currentItem, payload.update.metadata, metadataVersion, vueSet, vueDeleter);
          } else if (payload.update[prop] === undefined) {
            Vue.delete(currentItem, prop);
          } else {
            Vue.set(currentItem, prop, payload.update[prop]);
          }
        }
      },
      getPlayer(): ItemMediaPlayerOps {
        return quickPreviewMediaPlayerOps;
      }
    }
  };

  return quickPreview;
}

/**
 * If previewable, track recently previewed items. If an item is already in the list, to avoid duplicates, we remove the old copy
 * and add a new clone to the top of the list. Items may have changed in the meantime.
 *
 */
function addToRecentlyPreviewed(recentlyPreviewedList: (Item | RelatedItem)[], itemToAdd: Item | RelatedItem) {


  if (!isPreviewable(itemToAdd)) {
    return;
  }

  const indexOfExisting = recentlyPreviewedList.findIndex(item => item.id === itemToAdd.id);
  if (indexOfExisting >= 0) {
    recentlyPreviewedList.splice(indexOfExisting, 1);
  }
  recentlyPreviewedList.unshift(cloneDeep(itemToAdd));
}

function isPreviewable(item: Item | RelatedItem): boolean {
  return item.itemType !== ItemType.FOLDER;
}

function isThumbnailStripClick(event: ThumbnailStripClick | ThumbnailClick): event is ThumbnailStripClick {
  return 'percentageOfVideo' in event;
}
